import { select } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import { PaaMilestoneProps, paaMilestonesInitialState } from './milestones.store';
import { Store } from '@ngneat/elf';
import { PaaMilestone } from '@/types';
import { dateUtils } from '@/utils/date.utils';
import { BaseStoreRepository } from '@/store/base-store.repository';

export type PaaMileStoneStore = Store<
  {
    name: string;
    state: PaaMilestoneProps;
    config: undefined;
  },
  PaaMilestoneProps
>;

@Injectable({ providedIn: 'root' })
export class PaaMilestonesRepositoryFactory {
  static create(store: PaaMileStoneStore) {
    return new PaaMilestoneRepository(store);
  }
}

export class PaaMilestoneRepository extends BaseStoreRepository<PaaMilestoneProps> {
  constructor(store: PaaMileStoneStore) {
    super(store.pipe(select(state => state)), store, { ...paaMilestonesInitialState });
  }

  setMilestoneDto(response: PaaMilestone): void {
    this.set('milestone', response);
    this.set('milestoneDto', {
      id: response.id,
      realisationEnvisagees: response.realisationEnvisagees,
      'service.id': response.service?.id,
      beneficiaire: response.beneficiaire || '',
      'typeMarcher.id': response.typeMarcher?.id,
      'modePassation.id': response.modePassation?.id,
      'imputationBudgetaire.id': response.imputationBudgetaire?.id,
      datePrevLps: dateUtils.normalizeDate(new Date(response.datePrevLps)),
      datePrevAc: dateUtils.normalizeDate(new Date(response.datePrevAc)),
      lots: response.lots.map(lot => ({ ...lot, budget: lot.budget ? lot.budget : undefined })).sort((a, b) => a.id - b.id),
      financements: response.financements.map(financement => ({
        id: financement.financementId,
        budgetEstimer: financement.budgetEstimer,
        monnaieId: financement.monnaieId,
        sourceFinancement: { id: financement.sourceFinancement?.id },
      })),
      docIds: response.docs.map(doc => doc.id),
      realisationFiles: [],
    });
    this.set('initialMilestoneDto', structuredClone(this.get('milestoneDto')));

    const keyMap = {
      serviceIdSelected: 'service',
      typeOfMarketIdSelected: 'typeMarcher',
      selectionModeIdSelected: 'modePassation',
      imputationBudgetaireIdSelected: 'imputationBudgetaire',
    } as const;

    for (const [storeKey, responseKey] of Object.entries(keyMap)) {
      const item = response[responseKey];
      this.set(storeKey as keyof PaaMilestoneProps, { name: item?.description, code: item?.id });
    }
  }

  removeSlotError(index: number, field: string) {
    const slotErrors = [...(this.get<any>('slotErrors') || [])];

    if (!slotErrors || !slotErrors[index]) return;

    const updatedErrors = [...slotErrors];
    const slotError = { ...updatedErrors[index] };

    slotError[field] = undefined;

    if (Object.keys(slotError).length === 0) {
      updatedErrors.splice(index, 1);
    } else {
      updatedErrors[index] = slotError;
    }
    this.set('slotErrors', updatedErrors);
  }

  removeFundingError(index: number, field: string) {
    const fundingErrors = [...(this.get<any>('fundingErrors') || [])];

    if (!fundingErrors || !fundingErrors[index]) return;

    const updatedErrors = [...fundingErrors];
    const fundingError = { ...updatedErrors[index] };

    fundingError[field] = undefined;

    if (Object.keys(fundingError).length === 0) {
      updatedErrors.splice(index, 1);
    } else {
      updatedErrors[index] = fundingError;
    }
    this.set('fundingErrors', updatedErrors);
  }
}
